import React, { useEffect, useMemo, useRef, useState } from "react";
import { IconSearch, IconX } from "@tabler/icons-react";

/**
 * Ékezet- és kisbetű-független keresőkulcs.
 */
export const normalizeText = (value) =>
    String(value ?? "")
        .toLowerCase()
        .normalize("NFD")
        .replace(/[̀-ͯ]/g, "")
        .replace(/\s+/g, " ")
        .trim();

/**
 * A `shoe_color_options` (cselicoloroptions × cselishoecolor × cselitopleather) sorok egységes alakra hozása.
 *
 * Az Elit a színeket kóddal azonosítja (bőrfajta betűje + sorszám, pl. B29, N12, V4). A kód a DB-ben vagy a szín
 * kódjában van teljesen, vagy a bőr kódja + szín kódja összefűzve adja; mindkettőre keresünk.
 */
export const normalizeShoeColorOptions = (raw) =>
    (raw || [])
        .map((c) => {
            const id = Number(c.cselicolor_szam) || null;
            const name = c.cselicolor_color_national || c.cselicolor_color_name || "Ismeretlen szín";
            const leather = c.cselicolor_top_leather_name || "";
            const colorCode = String(c.cselicolor_color_code || "").trim();
            const leatherCode = String(c.cselicolor_top_leather_code || "").trim();
            const combinedCode =
                leatherCode && colorCode && !colorCode.toLowerCase().startsWith(leatherCode.toLowerCase())
                    ? leatherCode + colorCode
                    : colorCode;
            const code = combinedCode || colorCode || leatherCode;
            const label = `${name}${leather ? " / " + leather : ""}`;
            const text = code ? `${code} ${label}` : label;

            return {
                id,
                name,
                leather,
                colorCode,
                leatherCode,
                code,
                label,
                // a rendelésben tárolt szöveg (hmeliorder_leather_color), a kóddal együtt
                text,
                color_1: Number(c.cselicolor_color_1) === 1,
                color_2: Number(c.cselicolor_color_2) === 1,
                color_3: Number(c.cselicolor_color_3) === 1,
                searchKey: normalizeText([code, colorCode, leatherCode + colorCode, name, leather].join(" ")),
                codeKeys: [code, colorCode, leatherCode + colorCode].filter(Boolean).map(normalizeText),
            };
        })
        .filter((c) => c.id);

/**
 * Megkeresi a listában a rendelésben tárolt színt: elsőként azonosító, másodikként a tárolt szöveg alapján
 * (régi rendeléseknél a szöveg kód nélkül, "Fekete / Boksz" alakban van).
 */
export const findStoredColor = (colors, storedId, storedText) => {
    const byId = Number(storedId) > 1 ? colors.find((c) => c.id === Number(storedId)) : null;
    if (byId) return byId;
    const t = String(storedText || "").trim();
    if (!t) return null;
    return colors.find((c) => c.text === t || c.label === t) || null;
};

/**
 * Tablet-barát színválasztó keresővel.
 * - gépelés közben szűr névre és kódra (ékezet nélkül is),
 * - pontos kódegyezésnél (pl. "B29") azonnal kiválasztja a színt, ahogy a desktop méretlap-modul,
 * - a kiválasztott elem kiemelve és görgetve a látótérbe.
 */
const ColorPicker = ({
    colors = [],
    selectedId = null,
    onSelect,
    title = null,
    placeholder = "Keresés név vagy kód szerint (pl. B29)",
    maxHeight = 320,
    autoFocus = false,
    emptyText = "Nincs kiválasztva szín.",
}) => {
    const [query, setQuery] = useState("");
    const lastAutoSelected = useRef(null);
    const listRef = useRef(null);
    const inputRef = useRef(null);

    const q = normalizeText(query);

    const filtered = useMemo(() => (q ? colors.filter((c) => c.searchKey.includes(q)) : colors), [colors, q]);

    const selected = useMemo(() => colors.find((c) => c.id === selectedId) || null, [colors, selectedId]);

    // pontos kódegyezés -> azonnali kiválasztás
    useEffect(() => {
        if (!q) {
            lastAutoSelected.current = null;
            return;
        }
        const exact = colors.filter((c) => c.codeKeys.includes(q));
        if (exact.length === 1 && exact[0].id !== selectedId && lastAutoSelected.current !== q) {
            lastAutoSelected.current = q;
            onSelect(exact[0]);
        }
    }, [q, colors, selectedId, onSelect]);

    // a kiválasztott elem legyen látható a listában
    useEffect(() => {
        if (!selectedId || !listRef.current) return;
        const el = listRef.current.querySelector(`[data-color-id="${selectedId}"]`);
        if (el && typeof el.scrollIntoView === "function") {
            el.scrollIntoView({ block: "nearest" });
        }
    }, [selectedId, filtered.length]);

    const handleKeyDown = (e) => {
        // Enter: ha egyetlen találat van, azt választjuk
        if (e.key === "Enter") {
            e.preventDefault();
            if (filtered.length === 1 && filtered[0].id !== selectedId) {
                onSelect(filtered[0]);
            }
        }
        if (e.key === "Escape") {
            setQuery("");
        }
    };

    return (
        <div
            className="p-3 h-100 d-flex flex-column"
            style={{
                border: "1px solid #e2e8f0",
                backgroundColor: "#f9fafb",
                boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
            }}
        >
            {title && <h6 className="mb-2 text-center">{title}</h6>}

            <div
                className="mb-2"
                style={{
                    padding: "10px",
                    backgroundColor: selected ? "#e8f5e0" : "#ffffff",
                    border: `1px solid ${selected ? "#81c94a" : "#e5e7eb"}`,
                    fontSize: "15px",
                    fontWeight: 600,
                    color: "black",
                    textAlign: "center",
                    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
                }}
            >
                {selected ? (
                    <>
                        Kiválasztott szín:{" "}
                        {selected.code && <span className="badge bg-dark me-2">{selected.code}</span>}
                        {selected.label}
                    </>
                ) : (
                    emptyText
                )}
            </div>

            <div className="input-group mb-2">
                <span className="input-group-text">
                    <IconSearch size={20} />
                </span>
                <input
                    ref={inputRef}
                    type="text"
                    className="form-control form-control-lg"
                    placeholder={placeholder}
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyDown={handleKeyDown}
                    autoFocus={autoFocus}
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="characters"
                    spellCheck={false}
                    aria-label="Szín keresése"
                />
                {query && (
                    <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={() => {
                            setQuery("");
                            inputRef.current?.focus();
                        }}
                        aria-label="Keresés törlése"
                    >
                        <IconX size={18} />
                    </button>
                )}
            </div>

            <div className="text-muted small mb-1 text-end">
                {filtered.length} / {colors.length} szín
            </div>

            <div
                ref={listRef}
                className="scrollable-div"
                style={{
                    maxHeight: `${maxHeight}px`,
                    overflowY: "auto",
                    border: "1px solid #e2e8f0",
                    padding: "10px",
                    backgroundColor: "#ffffff",
                    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                }}
            >
                {filtered.length === 0 ? (
                    <div className="text-center text-muted py-3">Nincs a keresésnek megfelelő szín.</div>
                ) : (
                    filtered.map((color) => {
                        const isSelected = selectedId === color.id;
                        return (
                            <div
                                key={color.id}
                                data-color-id={color.id}
                                role="button"
                                className={`color-option d-flex align-items-center ${isSelected ? "selected" : ""}`}
                                style={{
                                    padding: "12px 10px",
                                    marginBottom: "6px",
                                    cursor: "pointer",
                                    border: "1px solid #e2e8f0",
                                    backgroundColor: isSelected ? "#81c94a" : "#f8fafc",
                                    color: "black",
                                    fontWeight: isSelected ? "600" : "500",
                                    boxShadow: isSelected ? "0 2px 4px rgba(0, 0, 0, 0.2)" : "0 1px 2px rgba(0, 0, 0, 0.1)",
                                }}
                                onClick={() => onSelect(color)}
                            >
                                {color.code && (
                                    <span
                                        className="badge me-2"
                                        style={{
                                            backgroundColor: isSelected ? "#2f5d1a" : "#334155",
                                            minWidth: "52px",
                                            fontSize: "13px",
                                        }}
                                    >
                                        {color.code}
                                    </span>
                                )}
                                <span>{color.label}</span>
                            </div>
                        );
                    })
                )}
            </div>
        </div>
    );
};

export default ColorPicker;
